'use client'

import type { IconButtonProps } from '@chakra-ui/react'
import { Box, Group, IconButton, Menu, Portal, Show } from '@chakra-ui/react'
import { ThemeProvider, useTheme } from 'next-themes'
import { forwardRef, useEffect, useState } from 'react'
import { LuList, LuMoon, LuMoonStar, LuSun, LuSunDim, LuSunMedium } from 'react-icons/lu'

type ColorMode = (typeof ALL_COLOR_MODES)[number]
type ThemeProviderProps = React.ComponentProps<typeof ThemeProvider>
type UseColorModeOptions = {
  restrictToLightDark?: boolean
}
interface ColorModeMenuProps {
  setColorMode: (theme: string) => void
}
type ColorModeButtonProps = Omit<IconButtonProps, 'aria-label'> & {
  restrictToLightDark?: boolean
}

const ALL_COLOR_MODES = ['light', 'dark', 'invert', 'sepia', 'grayscale'] as const
const LIGHT_DARK_MODES = ['light', 'dark'] as const

const THEME_MAP = {
  light: { icon: LuSun, name: 'Light' },
  dark: { icon: LuMoon, name: 'Dark' },
  invert: { icon: LuMoonStar, name: 'Invert' },
  sepia: { icon: LuSunMedium, name: 'Sepia' },
  grayscale: { icon: LuSunDim, name: 'Grayscale' },
}

function ColorModeMenu({ setColorMode }: ColorModeMenuProps) {
  return (
    <Menu.Root positioning={{ placement: 'left-start' }}>
      <Menu.Trigger asChild>
        <IconButton
          variant='outline'
          borderTopLeftRadius='0'
          borderBottomLeftRadius='0'
          aria-label='Change color mode'
          title='Change color mode'>
          <LuList />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            {ALL_COLOR_MODES.map((theme) => {
              const { icon: Icon, name } = THEME_MAP[theme]
              return (
                <Menu.Item key={theme} cursor='pointer' value={theme} onClick={() => setColorMode(theme)}>
                  <Icon />
                  <Box flex='1'>{name}</Box>
                </Menu.Item>
              )
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export function ColorModeProvider(props: ThemeProviderProps) {
  return (
    <ThemeProvider
      attribute='class'
      defaultTheme='system'
      themes={ALL_COLOR_MODES as unknown as string[]} // Cast to string[] for next-themes compatibility
      disableTransitionOnChange
      {...props}
    />
  )
}

export function useColorMode(options?: UseColorModeOptions) {
  const { resolvedTheme, setTheme } = useTheme()

  const toggleColorMode = () => {
    const themesToCycle = options?.restrictToLightDark ? LIGHT_DARK_MODES : ALL_COLOR_MODES
    const currentTheme = options?.restrictToLightDark
      ? (resolvedTheme as (typeof LIGHT_DARK_MODES)[number]) || 'light'
      : (resolvedTheme as ColorMode) || 'light'
    const currentIndex = (themesToCycle as readonly string[]).indexOf(currentTheme)
    const nextIndex = (currentIndex + 1) % themesToCycle.length

    setTheme(themesToCycle[nextIndex] ?? 'light')
  }

  return {
    colorMode: resolvedTheme as ColorMode | undefined,
    setColorMode: setTheme,
    toggleColorMode,
  }
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode()

  return colorMode === 'light' ? light : dark
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode()
  const IconComponent = colorMode ? THEME_MAP[colorMode]?.icon || LuSun : LuSun

  return <IconComponent />
}

export const ColorModeButton = forwardRef<HTMLButtonElement, ColorModeButtonProps>(function ColorModeButton(
  { restrictToLightDark, ...props },
  ref
) {
  const [mounted, setMounted] = useState(false)
  const { toggleColorMode, setColorMode } = useColorMode({ restrictToLightDark })

  useEffect(() => {
    // Ensure the component is mounted before accessing the theme
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  return (
    <>
      <Show when={restrictToLightDark}>
        <IconButton
          onClick={toggleColorMode}
          variant='ghost'
          aria-label='Toggle color mode'
          title='Toggle color mode'
          ref={ref}
          {...props}
          css={{
            _icon: {
              width: '5',
              height: '5',
            },
          }}>
          <ColorModeIcon />
        </IconButton>
      </Show>
      <Show when={!restrictToLightDark}>
        <Group attached>
          <IconButton
            onClick={toggleColorMode}
            variant='outline'
            aria-label='Toggle color mode'
            title='Toggle color mode'
            ref={ref}>
            <ColorModeIcon />
          </IconButton>
          <ColorModeMenu setColorMode={setColorMode} /> {/* Use the new component */}
        </Group>
      </Show>
    </>
  )
})
